<div class="profile-user profile-user--integrated">
  <div class="profile-layout">
    <article class="profile-panel">
      @if (profileUser$ | async; as profileUser) {
      <header class="profile-panel__header">
        <div class="profile-panel__identity">
          @if ({ meta: (profileImageMeta$ | async) }; as ctx) {
          <app-duna-profile-image
            class="profile-panel__photo"
            [obfuscatedName]="
              ctx.meta?.profile_image_obfuscated_name ??
              profileUser.profile_image_obfuscated_name ??
              null
            "
            [fallbackInitials]="getProfileInitials(profileUser)"
            [size]="72"
            [canEdit]="ctx.meta?.can_edit_profile_image ?? false"
            [scopeType]="'user'"
            [scopeId]="profileUser.id ?? undefined"
            [entityUuid]="profileUser.uuid"
            [entityKind]="'user'"
            (obfuscatedNameChange)="onProfileImageChanged()"
          ></app-duna-profile-image>
          }
          <div class="profile-panel__intro">
            <h1 class="profile-panel__name">{{ fullName$ | async }}</h1>
            @if ((availableYears$ | async)?.length) {
            <div class="profile-panel__year">
              <mat-form-field appearance="outline" subscriptSizing="dynamic">
                <mat-label>År</mat-label>
                <mat-select
                  [value]="selectedYear$ | async"
                  (selectionChange)="onYearChange($event.value)"
                >
                  @for (year of availableYears$ | async; track year) {
                  <mat-option [value]="year">{{ year }}</mat-option>
                  }
                </mat-select>
              </mat-form-field>
            </div>
            }
          </div>
        </div>
        @if (profileProgressSummary$ | async; as progress) {
        <app-duna-user-progress-label
          class="profile-panel__progress"
          [paths]="progress.paths"
          [countModuleOnce]="countModuleOnce"
          [totals]="{
            maintenance_minutes_required_total: progress.totals.required,
            maintenance_minutes_completed_total: progress.totals.completed,
            maintenance_minutes_intended_total: progress.totals.intended,
            registered_percentage: progress.totals.registeredPercentage,
            progress_percentage: progress.totals.progressPercentage
          }"
          [year]="progress.year"
        ></app-duna-user-progress-label>
        }
      </header>
      }

      <section class="profile-panel__paths" aria-labelledby="profile-education-heading">
        <div class="profile-panel__paths-head">
          <h2 id="profile-education-heading">Din uddannelse</h2>
        </div>

        @if (pathsSection.loading) {
        <div class="profile-panel__state loading-paths">
          <mat-progress-bar mode="indeterminate" class="paths-progress-bar"></mat-progress-bar>
          <p class="loading-text">Indlæser uddannelsesstier...</p>
        </div>
        } @else if (pathsSection.error) {
        <div class="profile-panel__state error-message">
          <mat-icon color="warn">error</mat-icon>
          <p>{{ pathsSection.errorMessage }}</p>
          <button mat-button color="primary" (click)="retryLoading()">
            <mat-icon>refresh</mat-icon> Prøv igen
          </button>
        </div>
        } @else if (pathsSection.paths.length === 0) {
        <div class="profile-panel__state no-data-message">
          <mat-icon>info</mat-icon>
          <p>Ingen uddannelsesstier fundet for det valgte år.</p>
        </div>
        } @else {
        <ul class="path-list">
          @for (path of pathsSection.paths; track trackPathRow($index, path); let i = $index) {
          <li class="path-list__item" [attr.data-path-id]="path.path_id">
            <div class="path-list__main">
              <div class="path-list__lead">
                <span class="path-list__monogram" aria-hidden="true">{{
                  path.name | slice : 0 : 3
                }}</span>
                <div class="path-list__titles">
                  <span class="path-list__name">{{
                    path.full_name || path.name
                  }}</span>
                  @if (shouldShowMaintenanceStartDate(path)) {
                  <span class="path-list__meta">{{
                    getMaintenanceStartDate(path) | date : 'dd/MM-yyyy'
                  }}</span>
                  } @else if (path.assigned_at) {
                  <span class="path-list__meta">{{
                    path.assigned_at | date : 'dd/MM-yyyy'
                  }}</span>
                  }
                </div>
                @if (hasCrossOrgVersion(path)) {
                <mat-icon
                  class="path-list__cross-org"
                  matTooltip="Fra anden organisation"
                  matTooltipPosition="above"
                  >warning</mat-icon
                >
                }
              </div>

            <div class="path-list__badges">
              @if (pathRequiresMaintenance(path) &&
              !hasMeritForCurrentPeriod(path)) {
              <span
                class="current-year-badge"
                [ngClass]="getCurrentYearStatus(path)"
                [matTooltip]="
                  getCurrentYearStatusTooltip(getCurrentYearStatus(path))
                "
              >
                <mat-icon class="status-icon">{{
                  getCurrentYearStatusIcon(getCurrentYearStatus(path))
                }}</mat-icon>
                {{ getCurrentYearStatusText(getCurrentYearStatus(path), path) }}
              </span>
              } @if (pathRequiresMaintenance(path) &&
              hasMeritForCurrentPeriod(path)) {
                <span
                  class="merit-badge"
                  [matTooltip]="'Brugeren har merit for ' + getProfileYear()"
                  matTooltipPosition="above"
                >
                  <mat-icon class="status-icon">verified</mat-icon>
                  Merit
                </span>
                }
                <app-duna-validation-badge
                  [status]="getDunaValidationStatus(path)"
                  [customTooltip]="
                    getDunaValidationTooltip(getDunaValidationStatus(path), path)
                  "
                >
                </app-duna-validation-badge>
              </div>
            </div>

            <div class="path-list__footer">
              @if (pathRequiresMaintenance(path)) {
              <div
                class="path-list__maintenance"
                [matTooltip]="getMaintenanceBarTooltip(path)"
              matTooltipClass="duna-maintenance-bar-tooltip"
              >
                <span class="maintenance-label">Vedligeholdelse</span>
                <div class="segmented-bar path-list__bar">
                  <div
                    class="bar-fill completed"
                    [style.width.%]="getMaintenanceProgressPercentage(path)"
                  ></div>
                  <div
                    class="bar-fill intended"
                    [style.width.%]="getMaintenanceRegisteredBarExtraPercentage(path)"
                  ></div>
                </div>
                <span class="maintenance-text">
                  {{
                    getMaintenanceCompletedMinutesDisplayed(path) / 60
                      | number : '1.0-1'
                  }}/{{ path.maintenance_minutes_required / 60 | number : '1.0-1' }}t
                </span>
              </div>
              } @else {
              <span class="path-list__maintenance path-list__maintenance--empty"></span>
              }
              <button
                class="path-list__details"
                mat-button
                type="button"
                (click)="openPathDetailDialog(path, i, $event)"
              >
                Se detaljer
                <mat-icon iconPositionEnd>chevron_right</mat-icon>
              </button>
            </div>
          </li>
          }
        </ul>
        }
    </section>
    </article>

    @if (showProfileSideColumn) {
    <div class="profile-layout__aside">
      <section class="profile-surface profile-surface--events" aria-labelledby="profile-events-heading">
        <div class="profile-surface__header">
          <mat-icon>event</mat-icon>
          <h2 id="profile-events-heading">Dine events</h2>
        </div>
        <div class="profile-surface__body">
          <app-dynamic-table [dynamicInput]="eventsTableInput"></app-dynamic-table>
        </div>
      </section>

      <section class="profile-surface profile-surface--stations" aria-labelledby="profile-stations-heading">
        <div class="profile-surface__header">
          <mat-icon>location_on</mat-icon>
          <h2 id="profile-stations-heading">Mine stationer</h2>
        </div>
        <div class="profile-surface__body profile-surface__body--stations">
          <app-duna-mine-stationer></app-duna-mine-stationer>
        </div>
      </section>
    </div>
    }
  </div>
</div>

<style>
  .loading-paths {
    padding: 20px;
    text-align: center;
    margin: 0;
  }

  .paths-progress-bar {
    max-width: 280px;
    margin: 0 auto 10px;
  }

  .loading-text {
    color: #666;
    margin: 0;
    font-size: 14px;
  }

  .error-message {
    padding: 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
  }

  .error-message mat-icon {
    font-size: 24px;
    height: 24px;
    width: 24px;
  }

  .error-message p {
    color: #d32f2f;
    margin: 0;
    font-size: 14px;
  }
</style>
